import { useState, useEffect, useRef } from 'react'
import { useMutation } from "@tanstack/react-query";
import Project from '../../../models/Project';
import { useProject } from '../../../providers/ClientProjectProvider';
import { useSearchParams } from "react-router-dom";
import { Modal, Radio, Input } from "antd";
import Form, { FormInputComponent } from "../../../components/form/Form";
// import LoadingSpinner from '../../../components/LoadingSpinner';

const EXPECTATION_OPTIONS = {
    1: 'Far short of expectations',
    2: 'Short of expectations',
    3: 'Met expectations',
    4: 'Exceeded expectations',
    5: 'Greatly exceeded expectations'
}

const SATISFACTION_OPTIONS = {
    1: 'Very dissatisfied',
    2: 'Dissatisfied',
    3: 'Neither satisfied nor dissatisfied',
    4: 'Satisfied',
    5: 'Very satisfied'
}


const getExistingReviews = project => {
    const initialReviews = {};
    project.positions?.forEach(pos => {
        const latestReview = pos.assigned_candidate?.job_candidate?.talent_opps?.[0]?.latest_review;
        if (latestReview) {
            initialReviews[pos.id] = {
                rating: latestReview.rating,
                notes: latestReview.notes || '',
                talentId: pos.assigned_candidate.id,
                edited: false,
            };
        }
    });

    return initialReviews;
}

const ClientSatisfactionSurveyModal = () => {
    const { project, isProjectUpdating: isDisabled } = useProject();
    const [searchParams, setSearchParams] = useSearchParams();

    const [formModel, setFormModel] = useState({
        satisfaction_score: null,
        expectation_score: null,
        recommend_score: null,
        notes: ''
    });

    // State for talent review section
    const [selectedTalent, setSelectedTalent] = useState(null);
    const [talentReviews, setTalentReviews] = useState({});
    const [currentReview, setCurrentReview] = useState({ rating: null, notes: '' });

    // Ref for scrolling to top of modal content
    const modalContentRef = useRef(null);

    // Initialize talentReviews with existing reviews from project data
    useEffect(() => {
        setTalentReviews(getExistingReviews(project));
    }, [project?.id]);

    // Scroll to top when a talent is selected
    useEffect(() => {
        if (selectedTalent && modalContentRef.current) {
            modalContentRef.current.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }, [selectedTalent]);

    // Calculate if survey fields are required
    const hasEditedReviews = Object.values(talentReviews).some(review => review.edited === true);
    const hasSurveyValues = formModel.satisfaction_score || formModel.expectation_score || formModel.recommend_score || formModel.notes;
    const surveyFieldsRequired = !hasEditedReviews || !!hasSurveyValues;

    const submitSatisfactionSurveyMutation = useMutation(['submitSatisfactionSurvey', project?.id],
        async data => {
            await TB.wait(1000)

            // Check if any reviews have been edited
            const hasEditedReviews = Object.values(talentReviews).some(review => review.edited === true);

            // Check if any survey field has a value
            const hasSurveyValues = data.satisfaction_score || data.expectation_score || data.recommend_score || data.notes;

            // Validation logic
            if (!hasEditedReviews && !hasSurveyValues) {
                // If no reviews edited, survey fields must be filled out
                TB.toast('Please complete the survey or review at least one talent.', { type: 'error' });
                throw new Error('Survey fields are required when no reviews are edited.');
            }

            if (hasSurveyValues) {
                // If any survey field has value, all required fields must be filled
                if (!data.satisfaction_score || !data.expectation_score || !data.recommend_score) {
                    TB.toast('Please complete all required survey fields.', { type: 'error' });
                    throw new Error('All required survey fields must be filled.');
                }
            }

            // Format talent reviews
            const reviews = Object.keys(talentReviews)
                .filter(positionId => talentReviews[positionId].edited === true)
                .map(positionId => ({
                    position_id: Number(positionId),
                    rating: Number(talentReviews[positionId].rating),
                    notes: talentReviews[positionId].notes || '',
                    talent_user_id: talentReviews[positionId].talentId,
                }));

            // Build formattedData based on survey field values
            const formattedData = {
                survey: hasSurveyValues ? {
                    satisfaction_score: Number(data.satisfaction_score),
                    expectation_score: Number(data.expectation_score),
                    recommend_score: Number(data.recommend_score),
                    notes: data.notes || '',
                } : null,
                reviews: reviews
            }

            console.log(formattedData, ' FORMATTED DATA')
            const res = await Project.submitSatisfactionSurvey(project.id, formattedData)
            console.log(res, 'res from backend')
            return res;
        },
        {
            onSuccess: async res => {
                console.log(res, 'eerrrsss')
                TB.toast('Survey submitted successfully!', { type: 'success' });
                closeSurveyModal(true)
            },
            onError: async (e) => {
                console.log('onError', e)
                await TB.showServerErrorToast();
            }
        }
    )


    const closeSurveyModal = (onSubmitSuccess = false) => {
        setSearchParams(prevParams => {
            const newParams = new URLSearchParams(prevParams);
            newParams.delete('submitReviewSurvey');
            return newParams;
        });
        setSelectedTalent(null);
        if (!onSubmitSuccess) {
            setTalentReviews(getExistingReviews(project));
        } else {
            const updatedTalentReviews = Object.fromEntries(
                Object.entries(talentReviews).map(([key, value]) => [key, { ...value, edited: false }])
            );
            setTalentReviews(updatedTalentReviews)
        }

        setFormModel({
            ...formModel,
            satisfaction_score: null,
            expectation_score: null,
            recommend_score: null,
            notes: '',
        })
    }


    return (
        <div>
            <button
                type="button"
                disabled={isDisabled}
                className="whitespace-nowrap btn btn-primary px-3 py-2 text-sm"
                onClick={() => {
                    setSearchParams(prevParams => {
                        const newParams = new URLSearchParams(prevParams);
                        newParams.set('submitReviewSurvey', 'true');
                        return newParams;
                    });
                }}
            >
                Take Survey
            </button>


            <Modal
                open={searchParams.has('submitReviewSurvey')}
                onCancel={() => closeSurveyModal(false)}
                title={selectedTalent ? `Review ${selectedTalent.first_name} ${selectedTalent.last_name}` : "Project Satisfaction Survey"}
                width={800}
                centered
                footer={null}
                destroyOnClose
                closable={!submitSatisfactionSurveyMutation.isLoading && !selectedTalent}
                maskClosable={!submitSatisfactionSurveyMutation.isLoading && !Object.values(talentReviews).some(item => item.edited === true)}
            >
                <div className="relative overflow-hidden" ref={modalContentRef}>
                    <div
                        className="transition-transform duration-300 ease-in-out flex"
                        style={{ transform: selectedTalent ? 'translateX(-100%)' : 'translateX(0)' }}
                    >
                        {/* Main Survey Form */}
                        <div className="w-full flex-shrink-0">
                            <Form
                                title={null}
                                subtitle={null}
                                showRequired
                                minimal
                                submitButton={false}
                                onSubmit={() => submitSatisfactionSurveyMutation.mutateAsync(formModel)}
                                // onChange={model => setFormModel({ ...formModel, ...model })}
                                onChange={setFormModel}
                                model={formModel}
                                fields={[]}
                                isDisabled={isDisabled}
                            >
                                {/* {submitSatisfactionSurveyMutation.isLoading && <LoadingSpinner />} */}
                                <div className="flex flex-col gap-6 pb-4">
                                    <p className="text-base">
                                        Based on your most recent project, please complete this quick survey to let us know how it went. If your project is still underway or has been extended, let us know how it's going.
                                    </p>

                                    {hasEditedReviews && !hasSurveyValues && (
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                            <p className="text-sm text-blue-800">
                                                <i className="fas fa-info-circle mr-2"></i>
                                                Survey questions are optional when submitting talent reviews.
                                            </p>
                                        </div>
                                    )}

                                    <FormInputComponent
                                        fieldName="satisfaction_score"
                                        value={formModel.satisfaction_score}
                                        label="1. Please rate your level of satisfaction with the upfront process to get your project ready for kick-off (talent matching, contracting, talent onboarding)."
                                        type="antd-input"
                                        required={surveyFieldsRequired}
                                        className="!mb-0"
                                    >
                                        <Radio.Group
                                            className="flex flex-wrap gap-4 p-3"
                                            name="satisfaction_score"
                                        >
                                            {Object.keys(SATISFACTION_OPTIONS).map(v => {
                                                return (
                                                    <Radio key={v} value={v} className="text-xs flex-1">{SATISFACTION_OPTIONS[v]}</Radio>
                                                )
                                            })}
                                        </Radio.Group>
                                    </FormInputComponent>


                                    <FormInputComponent
                                        fieldName="expectation_score"
                                        value={formModel.expectation_score}
                                        label="2. Overall, how well did the project meet your expectations? If your project is still underway or has been extended, how well is it meeting your expectations?"
                                        type="antd-input"
                                        required={surveyFieldsRequired}
                                        className="!mb-0"
                                    >
                                        <Radio.Group
                                            className="flex flex-wrap gap-4 p-3"
                                            name="expectation_score"
                                        >
                                            {Object.keys(EXPECTATION_OPTIONS).map(v => {
                                                return (
                                                    <Radio key={v} value={v} className="text-xs flex-1">{EXPECTATION_OPTIONS[v]}</Radio>
                                                )
                                            })}
                                        </Radio.Group>
                                    </FormInputComponent>


                                    <FormInputComponent
                                        fieldName="recommend_score"
                                        value={formModel.recommend_score}
                                        // label="3. How likely is it that you would recommend Wripple to a friend or colleague?"
                                        label="3. On a scale of 0 to 10, where 0 means “not at all likely” and 10 means “extremely likely,” how likely is it that you would recommend Wripple to a friend of colleague?"
                                        type="antd-input"
                                        required={surveyFieldsRequired}
                                        className="!mb-0"
                                    >
                                        <Radio.Group value={formModel.recommend_score} buttonStyle="solid" className="pt-3 flex flex-wrap">
                                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                                <Radio.Button key={num} value={num} className="flex-1 text-center">{num}</Radio.Button>
                                            ))}
                                        </Radio.Group>

                                    </FormInputComponent>

                                    <FormInputComponent
                                        fieldName="notes"
                                        value={formModel.notes}
                                        label="4. Please use the box below to provide any additional feedback you'd like to share about your experience."
                                        placeholder="Enter your feedback here..."
                                        type="antd-input"
                                        required={false}
                                        maxLength={2000}
                                    >
                                        <Input.TextArea rows={4} />
                                    </FormInputComponent>

                                    <div className="border-t pt-4">
                                        <p className="font-medium text-gray-900 mb-3">Review Talent (Optional)</p>

                                        <div className="flex flex-col gap-3">
                                            {project.positions.map(pos => {
                                                const hasReview = talentReviews[pos.id];
                                                return (
                                                    <button
                                                        key={pos.id}
                                                        type="button"
                                                        onClick={() => {
                                                            setSelectedTalent({ ...pos.assigned_candidate, positionId: pos.id, talentId: pos.assigned_candidate.id });
                                                            // Load existing review if it exists
                                                            const existingReview = talentReviews[pos.id];
                                                            setCurrentReview({
                                                                rating: existingReview?.rating || null,
                                                                notes: existingReview?.notes || '',
                                                            });
                                                        }}
                                                        className="bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg p-4 w-full flex items-center justify-between gap-3 transition-colors cursor-pointer text-left"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            <img src={pos.assigned_candidate.image_path} className="h-12 w-12 object-cover rounded-full" alt="" />
                                                            <div>
                                                                <p className="font-medium text-gray-900">{pos.assigned_candidate.first_name} {pos.assigned_candidate.last_name}</p>
                                                                <p className="text-xs text-gray-600">{pos.title}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            {hasReview ? (
                                                                <>
                                                                    {hasReview.edited ? (
                                                                        <span className="text-xs text-yellow-600 font-medium"><i className="fas fa-check" /> Pending Submit</span>
                                                                    ) : (
                                                                        <span className="text-xs text-green-600 font-medium"><i className="fas fa-check" /> Reviewed</span>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <span className="text-xs text-gray-500">Not yet reviewed</span>
                                                            )}
                                                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                            </svg>
                                                        </div>
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-4 justify-end mt-6">
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        disabled={isDisabled || submitSatisfactionSurveyMutation.isLoading}
                                        onClick={() => closeSurveyModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isDisabled || submitSatisfactionSurveyMutation.isLoading}
                                    >
                                        Submit Survey
                                    </button>
                                </div>
                            </Form>
                        </div>

                        {/* Talent Review Section */}
                        <div className="w-full flex-shrink-0 px-2">
                            {selectedTalent && (
                                <>
                                    {/* Back button */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedTalent(null);
                                            setCurrentReview({ rating: null, notes: '' });
                                        }}
                                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors -ml-2 mb-6"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                        <span className="text-sm font-medium">Back to Survey</span>
                                    </button>

                                    <Form
                                        title={null}
                                        subtitle={null}
                                        showRequired
                                        minimal
                                        submitButton={false}
                                        onSubmit={(data) => {
                                            // Save review to talentReviews
                                            setTalentReviews(prev => ({
                                                ...prev,
                                                [selectedTalent.positionId]: {
                                                    rating: data.rating,
                                                    notes: data.notes || '',
                                                    talentId: selectedTalent.talentId,
                                                    edited: true,
                                                }
                                            }));
                                            setSelectedTalent(null);
                                            setCurrentReview({ rating: null, notes: '' });
                                            // TB.toast('Review saved', { type: 'success' });
                                        }}
                                        onChange={setCurrentReview}
                                        model={currentReview}
                                        fields={[]}
                                        isDisabled={false}
                                    >
                                        {/* Talent info */}
                                        <div className="flex items-center gap-4 pb-4 mb-6 border-b">
                                            <img src={selectedTalent.image_path} className="h-16 w-16 object-cover rounded-full" alt="" />
                                            <div>
                                                <h3 className="font-semibold text-lg text-gray-900">
                                                    {selectedTalent.first_name} {selectedTalent.last_name}
                                                </h3>
                                                <p className="text-sm text-gray-600">
                                                    How would you rate your experience working with this talent?
                                                </p>
                                            </div>
                                        </div>

                                        {/* Rating */}
                                        <FormInputComponent
                                            fieldName="rating"
                                            value={currentReview.rating}
                                            label="Rating"
                                            type="antd-input"
                                            required
                                            className="mb-4"
                                        >
                                            <Radio.Group
                                                buttonStyle="solid"
                                                className="flex flex-wrap"
                                            >
                                                {/* {[
                                                    { val: 1, name: 'Poor' },
                                                    { val: 2, name: 'Fair' },
                                                    { val: 3, name: 'Good' },
                                                    { val: 4, name: 'Very Good' },
                                                    { val: 5, name: 'Excellent' }
                                                ].map(rating => (
                                                    <Radio.Button key={rating.val} value={rating.val} className="flex-1 text-center">
                                                        <div className="py-1">
                                                            <p className="font-semibold">{rating.val}</p>
                                                            <p className="text-xs">{rating.name}</p>
                                                        </div>
                                                    </Radio.Button>
                                                ))} */}
                                                {[1, 2, 3, 4, 5].map(rating => (
                                                    <Radio.Button key={rating} value={rating} className="flex-1 text-center">
                                                        {rating}
                                                    </Radio.Button>
                                                ))}
                                            </Radio.Group>
                                        </FormInputComponent>

                                        {/* Notes */}
                                        <FormInputComponent
                                            fieldName="notes"
                                            value={currentReview.notes}
                                            label="Additional Notes"
                                            placeholder="Share any specific feedback about your experience working with this talent..."
                                            type="antd-input"
                                            required={false}
                                            maxLength={1000}
                                        >
                                            <Input.TextArea rows={6} showCount />
                                        </FormInputComponent>

                                        {/* Action buttons */}
                                        <div className="flex gap-3 justify-end pt-4 mt-6 border-t">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedTalent(null);
                                                    setCurrentReview({ rating: null, notes: '' });
                                                }}
                                                className="btn btn-outline-primary"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn btn-primary"
                                            >
                                                Save Review
                                            </button>
                                        </div>
                                    </Form>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    )
}

export default ClientSatisfactionSurveyModal;